export default {
  name: 'APPROVAL_MODULE',
  state: {
    pendingApprovals: [],
    approvalHistory: [],
    currentApproval: null,
    loading: false,
    error: null,
    notifications: []
  },
  getters: {
    pendingApprovals: state => state.pendingApprovals,
    approvalHistory: state => state.approvalHistory,
    currentApproval: state => state.currentApproval,
    // isLoading: state => state.loading,
    hasError: state => !!state.error,
    errorMessage: state => state.error,
    notifications: state => state.notifications,
    unreadNotificationsCount: state => state.notifications.filter(n => !n.read).length,
    // getApiKey: state => state.apiKey
  },
  mutations: {
    SET_LOADING(state, isLoading) {
      state.loading = isLoading;
    },
    SET_ERROR(state, error) {
      state.error = error;
    },
    CLEAR_ERROR(state) {
      state.error = null;
    },
    SET_PENDING_APPROVALS(state, approvals) {
      state.pendingApprovals = approvals;
    },
    SET_APPROVAL_HISTORY(state, history) {
      state.approvalHistory = history;
    },
    SET_CURRENT_APPROVAL(state, approval) {
      state.currentApproval = approval;
    },
    UPDATE_APPROVAL_STATUS(state, { approvalId, status, ...details }) {
      // Update in pending approvals
      const pendingIndex = state.pendingApprovals.findIndex(a => a.id === approvalId);
      if (pendingIndex !== -1) {
        const updatedApproval = {
          ...state.pendingApprovals[pendingIndex],
          status,
          ...details
        };

        // Remove from pending and add to history
        state.pendingApprovals.splice(pendingIndex, 1);
        state.approvalHistory.unshift(updatedApproval);
      }

      // Update current approval if it matches
      if (state.currentApproval && state.currentApproval.id === approvalId) {
        state.currentApproval = {
          ...state.currentApproval,
          status,
          ...details
        };
      }
    },
    ADD_NOTIFICATION(state, notification) {
      state.notifications.unshift(notification);
    },
    MARK_NOTIFICATION_READ(state, notificationId) {
      const index = state.notifications.findIndex(n => n.id === notificationId);
      if (index !== -1) {
        state.notifications[index].read = true;
      }
    },
    CLEAR_NOTIFICATIONS(state) {
      state.notifications = [];
    }
  },
  actions: {
    // Fetch all pending expense requests
    async fetchPendingApprovals(context) {
      try {
        context.commit('SET_LOADING', true);
        context.commit('CLEAR_ERROR');

        const apiKey = context.rootGetters.getterApikey;
        const url = `${pettyCashApi}api/expense-requests/pending`;

        // Get user info to check permissions
        const user = context.rootGetters.CurrentUser;
        const canAccessAllBranches = user && user.permissions && user.permissions.includes('canAccessAllBranches');

        // Add branch filter if user doesn't have access to all branches
        let params = {};
        if (!canAccessAllBranches && user && user.branchId) {
          params.branchId = user.branchId;
        }

        const response = await axios.get(url, {
          headers: {
            'X-API-KEY': apiKey
          },
          params
        });

        // Transform the response to match the expected format
        const pendingRequests = response.data.map(request => ({
          id: request.id,
          requestId: request.id,
          requesterName: request.userName,
          title: request.expenseType,
          amount: request.amount,
          status: request.status === 'PENDING' ? '0' : (request.status === 'APPROVED' ? '1' : '2'),
          date: request.createdAt,
          description: request.reason,
          type: request.expenseType,
          subtype: request.subType,
          branchId: request.branchId,
          branchName: request.branchName,
          accountType: request.accountType,
          accountNumber: request.accountNumber,
          userId: request.userId
        }));

        context.commit('SET_PENDING_APPROVALS', pendingRequests);
        return pendingRequests;
      } catch (error) {
        context.commit('SET_ERROR', error.message || 'Failed to fetch pending approvals');
        errorHandler.tomcatError(error);
        throw error;
      } finally {
        context.commit('SET_LOADING', false);
      }
    },

    // Fetch approval history
    async fetchApprovalHistory(context) {
      try {
        context.commit('SET_LOADING', true);
        context.commit('CLEAR_ERROR');

        // TODO: Replace with actual API call when available
        const apiKey = context.getters.getApiKey;
        const url = `${pettyCashApi}approvals/history`;

        // For now, using mock data
        const response = await new Promise(resolve => {
          setTimeout(() => {
            resolve({
              data: [
                {
                  id: 3,
                  requestId: 103,
                  requesterName: 'Alice Johnson',
                  title: 'Software License',
                  amount: 299.00,
                  status: 'approved',
                  date: '2023-07-05',
                  approvedDate: '2023-07-06',
                  approvedBy: 'Current User',
                  description: 'Annual software license renewal',
                  images: []
                },
                {
                  id: 4,
                  requestId: 104,
                  requesterName: 'Bob Williams',
                  title: 'Conference Registration',
                  amount: 500.00,
                  status: 'declined',
                  date: '2023-07-01',
                  approvedDate: '2023-07-02',
                  approvedBy: 'Current User',
                  description: 'Industry conference registration fee',
                  declineReason: 'Insufficient details provided',
                  images: []
                }
              ]
            });
          }, 1000);
        });

        context.commit('SET_APPROVAL_HISTORY', response.data);
        return response.data;
      } catch (error) {
        context.commit('SET_ERROR', error.message || 'Failed to fetch approval history');
        errorHandler.tomcatError(error);
        throw error;
      } finally {
        context.commit('SET_LOADING', false);
      }
    },

    // Fetch a single expense request by ID
    async fetchApprovalById(context, approvalId) {
      try {
        context.commit('SET_LOADING', true);
        context.commit('CLEAR_ERROR');

        const apiKey = context.rootGetters.getterApikey;
        const url = `${pettyCashApi}api/expense-requests/${approvalId}`;

        const response = await axios.get(url, {
          headers: {
            'X-API-KEY': apiKey
          }
        });

        // Transform the response to match the expected format
        const request = response.data;
        const formattedRequest = {
          id: request.id,
          requestId: request.id,
          requesterName: request.userName,
          title: request.expenseType,
          description: request.reason,
          amount: request.amount,
          status: request.status === 'PENDING' ? '0' : (request.status === 'APPROVED' ? '1' : '2'),
          date: request.createdAt,
          type: request.expenseType,
          subtype: request.subType,
          branchId: request.branchId,
          branchName: request.branchName,
          accountType: request.accountType,
          accountNumber: request.accountNumber,
          userId: request.userId
        };

        context.commit('SET_CURRENT_APPROVAL', formattedRequest);
        return formattedRequest;
      } catch (error) {
        context.commit('SET_ERROR', error.message || 'Failed to fetch approval details');
        errorHandler.tomcatError(error);
        throw error;
      } finally {
        context.commit('SET_LOADING', false);
      }
    },

    // Approve an expense request
    async approveRequest(context, { approvalId, comments }) {
      try {
        context.commit('SET_LOADING', true);
        context.commit('CLEAR_ERROR');

        const apiKey = context.rootGetters.getterApikey;
        const url = `${pettyCashApi}api/expense-requests/${approvalId}/approve`;

        // Get current user ID
        const user = context.rootGetters.CurrentUser;
        if (!user || !user.id) {
          throw new Error('User information not available');
        }

        // Check if user has permission to approve expense requests
        const canApproveExpenseRequest = user.permissions && user.permissions.includes('canApproveExpenseRequest');
        if (!canApproveExpenseRequest) {
          throw new Error('You do not have permission to approve expense requests');
        }

        const response = await axios.post(url, null, {
          headers: {
            'X-API-KEY': apiKey
          },
          params: {
            processedBy: user.id
          }
        });

        // Update the approval status in the store
        const approvedDate = new Date().toISOString();
        context.commit('UPDATE_APPROVAL_STATUS', {
          approvalId,
          status: 'approved',
          approvedDate,
          approvedBy: user.fullName || user.username,
          comments: comments || ''
        });

        // Add notification
        context.commit('ADD_NOTIFICATION', {
          id: Date.now(),
          type: 'success',
          message: `Expense request #${approvalId} has been approved`,
          read: false,
          timestamp: new Date().toISOString()
        });

        return response.data;
      } catch (error) {
        context.commit('SET_ERROR', error.message || 'Failed to approve request');
        errorHandler.tomcatError(error);
        throw error;
      } finally {
        context.commit('SET_LOADING', false);
      }
    },

    // Decline an expense request
    async declineRequest(context, { approvalId, reason }) {
      try {
        context.commit('SET_LOADING', true);
        context.commit('CLEAR_ERROR');

        // Validate reason
        if (!reason) {
          throw new Error('Decline reason is required');
        }

        const apiKey = context.rootGetters.getterApikey;
        const url = `${pettyCashApi}api/expense-requests/${approvalId}/decline`;

        // Get current user ID
        const user = context.rootGetters.CurrentUser;
        if (!user || !user.id) {
          throw new Error('User information not available');
        }

        // Check if user has permission to approve expense requests
        const canApproveExpenseRequest = user.permissions && user.permissions.includes('canApproveExpenseRequest');
        if (!canApproveExpenseRequest) {
          throw new Error('You do not have permission to decline expense requests');
        }

        const response = await axios.post(url, null, {
          headers: {
            'X-API-KEY': apiKey
          },
          params: {
            processedBy: user.id,
            declineReason: reason
          }
        });

        // Update the approval status in the store
        const declinedDate = new Date().toISOString();
        context.commit('UPDATE_APPROVAL_STATUS', {
          approvalId,
          status: 'declined',
          declinedDate,
          declinedBy: user.fullName || user.username,
          declineReason: reason
        });

        // Add notification
        context.commit('ADD_NOTIFICATION', {
          id: Date.now(),
          type: 'info',
          message: `Expense request #${approvalId} has been declined`,
          read: false,
          timestamp: new Date().toISOString()
        });

        return response.data;
      } catch (error) {
        context.commit('SET_ERROR', error.message || 'Failed to decline request');
        errorHandler.tomcatError(error);
        throw error;
      } finally {
        context.commit('SET_LOADING', false);
      }
    },

    // Mark notification as read
    markNotificationAsRead(context, notificationId) {
      context.commit('MARK_NOTIFICATION_READ', notificationId);
    },

    // Clear all notifications
    clearNotifications(context) {
      context.commit('CLEAR_NOTIFICATIONS');
    }
  }
};
